import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControlLabel,
  Checkbox,
  Chip,
  Box,
} from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { campaignsActions } from '../../../modules/campaigns/campaigns.actions.js';
import { helper } from '../../../utils/helper.js';
import Constants from '../../../utils/constants.js';

/**
 * Détail des interventions d'une tournée (poste × campagne), en ordre de lavage réel.
 * Réutilise campaignsActions.fetchDetails(date, campagneId) → state.campaigns.details.
 * Clic sur une ligne → vue interventions du conteneur, ouverte dans un nouvel onglet.
 */
function ReportingInterventionsModal({ open, onClose, date, campagneId, tourneeLibelle, dispatch, details, loading, error }) {
  const [onlyImpossible, setOnlyImpossible] = useState(false);

  useEffect(() => {
    if (open && date && campagneId) {
      dispatch(campaignsActions.fetchDetails(date, campagneId));
    }
  }, [open, date, campagneId, dispatch]);

  const ordreReel = details?.ordre_reel ?? [];
  const filtered = onlyImpossible ? ordreReel.filter((c) => !c.isAccessible) : ordreReel;

  const openIntervention = (conteneurId) => {
    if (!conteneurId) return;
    const url = Constants.APP_ROUTE_INTERVENTIONS.replace(':id', conteneurId);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const statutChip = (c) => {
    const probs = c.problemes ?? c.problems ?? [];
    if (!c.isAccessible) return <Chip size="small" label="Impossible" sx={{ bgcolor: '#B22222', color: '#fff' }} />;
    if (probs.length > 0) return <Chip size="small" label="Problème" sx={{ bgcolor: '#e67e22', color: '#fff' }} />;
    return <Chip size="small" label="Lavé" sx={{ bgcolor: '#7bb421', color: '#fff' }} />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <span>Détail des interventions{tourneeLibelle ? ` — ${tourneeLibelle}` : ''}</span>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Typography color="error">Erreur lors du chargement du détail.</Typography>}
        {!loading && !error && (
          <>
            <FormControlLabel
              control={<Checkbox checked={onlyImpossible} onChange={(e) => setOnlyImpossible(e.target.checked)} />}
              label="Lavages impossibles uniquement"
            />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Conteneur</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Adresse</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Heure</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Problèmes</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#888' }}>
                      Aucune intervention à afficher
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((c, i) => {
                  const probs = c.problemes ?? c.problems ?? [];
                  return (
                    <TableRow
                      key={c.id ?? i}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openIntervention(c.id)}
                      title="Ouvrir la vue intervention dans un nouvel onglet"
                    >
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{c.nomenclature ?? '—'}</TableCell>
                      <TableCell>{c.adresse ?? '—'}</TableCell>
                      <TableCell>{c.date_evenement ? helper.getFormattedTime(c.date_evenement) : '—'}</TableCell>
                      <TableCell>{statutChip(c)}</TableCell>
                      <TableCell>{probs.length > 0 ? probs.join(', ') : '—'}</TableCell>
                      <TableCell>
                        <OpenInNew fontSize="small" color="action" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const mapStateToProps = (state) => ({
  details: state.campaigns.details,
  loading: state.campaigns.detailsLoading,
  error: state.campaigns.detailsError,
});

export default connect(mapStateToProps)(ReportingInterventionsModal);
